import { HttpBackend, HttpClient, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { normalizeStoredLanguage, readStoredLanguage } from '../i18n/language-storage';
import { CandidateAuthTokens } from '../models/candidate-auth.model';
import { CandidateTokenStorageService } from '../storage/candidate-token-storage.service';
import { CandidateTenantContextService } from '../tenant/candidate-tenant-context.service';

const REFRESH_PATH = '/candidate/Auth/refresh-token';
const AUTH_RETRY_HEADER = 'X-Candidate-Auth-Retry';
const NO_REFRESH_URL_PARTS = [
  '/candidate/Auth/register',
  '/candidate/Auth/login',
  '/candidate/Auth/refresh-token',
  '/candidate/Auth/forgot-password',
  '/candidate/Auth/reset-password',
];

let refreshInFlight: Promise<CandidateAuthTokens> | null = null;

function apiBase(): string {
  return environment.apiBaseUrl.replace(/\/+$/, '');
}

function isUnauthorized(error: HttpErrorResponse): boolean {
  if (error.status === 401) return true;
  const payload = error.error;
  return !!payload && typeof payload === 'object' && (payload as { statusCode?: number }).statusCode === 401;
}

function shouldSkipRefresh(req: HttpRequest<unknown>): boolean {
  if (req.headers.has(AUTH_RETRY_HEADER)) return true;
  return NO_REFRESH_URL_PARTS.some((part) => req.url.includes(part));
}

function extractTokens(body: unknown): CandidateAuthTokens | null {
  if (!body || typeof body !== 'object') return null;
  const root = body as { data?: unknown; accessToken?: string; refreshToken?: string };
  const payload = root.data && typeof root.data === 'object' ? (root.data as CandidateAuthTokens) : root;
  const accessToken = (payload as { accessToken?: string }).accessToken;
  const refreshToken = (payload as { refreshToken?: string }).refreshToken;
  return accessToken && refreshToken ? { accessToken, refreshToken } : null;
}

function redirectToLogin(router: Router, tenantContext: CandidateTenantContextService): void {
  void router.navigate(['/login'], { queryParams: tenantContext.getTenantQueryParams() });
}

function refreshSession(
  plainHttp: HttpClient,
  tokens: CandidateTokenStorageService,
  tenantContext: CandidateTenantContextService,
): Promise<CandidateAuthTokens> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = tokens.getRefreshToken();
      if (!refreshToken) {
        tokens.clear();
        throw new Error('Missing candidate refresh token');
      }

      const raw = await firstValueFrom(
        plainHttp.post<unknown>(
          `${apiBase()}${REFRESH_PATH}`,
          { tenantKey: tenantContext.getTenantKey(), refreshToken },
          {
            headers: {
              Accept: 'application/json',
              'Accept-Language': normalizeStoredLanguage(readStoredLanguage() ?? 'en-US'),
              'Content-Type': 'application/json',
              'X-Tenant-Key': tenantContext.getTenantKey(),
            },
          },
        ),
      );

      const nextTokens = extractTokens(raw);
      if (!nextTokens) {
        tokens.clear();
        throw new Error('Invalid candidate refresh response');
      }

      tokens.setTokens(nextTokens);
      return nextTokens;
    })().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

export const candidateAuthRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const tokens = inject(CandidateTokenStorageService);
  const backend = inject(HttpBackend);
  const router = inject(Router);
  const tenantContext = inject(CandidateTenantContextService);
  const plainHttp = new HttpClient(backend);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || shouldSkipRefresh(req) || !isUnauthorized(error)) {
        return throwError(() => error);
      }

      return from(refreshSession(plainHttp, tokens, tenantContext)).pipe(
        switchMap((nextTokens) =>
          next(
            req.clone({
              setHeaders: {
                Authorization: `Bearer ${nextTokens.accessToken}`,
                [AUTH_RETRY_HEADER]: '1',
              },
            }),
          ),
        ),
        catchError(() => {
          tokens.clear();
          redirectToLogin(router, tenantContext);
          return throwError(() => error);
        }),
      );
    }),
  );
};
